import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Bell, SignOut, CaretRight, CalendarCheck, BookOpen, Star, Megaphone, Sparkle, Images, Wallet, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStudent } from "@/context/StudentContext";
import { KidAvatar } from "@/components/KidAvatar";
import { GalleryCarousel } from "@/components/GalleryCarousel";
import { LearningMediaCarousel } from "@/components/LearningMediaCarousel";
import { ReactionThread } from "@/components/ReactionThread";
import { wibNow } from "@/lib/time";
import { toast } from "sonner";

const groupOf = (k) => (!k ? "" : k.startsWith("Kelas ") ? "Kelas " + k.split(" ")[1][0] : k.startsWith("TK B") ? "TK B" : k.startsWith("TK A") ? "TK A" : k.startsWith("KB") ? "KB" : "");
const inClass = (itemKelas, grp) => !grp || !itemKelas || itemKelas === "Semua" || groupOf(itemKelas) === grp;

export default function ParentHome() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const isSdqu = user?.school === "sdqu";
  const { active, students, selectStudent } = useStudent();
  const [surahList, setSurahList] = useState([]);
  const [tahfidz, setTahfidz] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [attToday, setAttToday] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [dailies, setDailies] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [videos, setVideos] = useState([]);
  const [juzProgress, setJuzProgress] = useState([]);
  const [notif, setNotif] = useState({ unread: 0, items: [] });
  const [notifOpen, setNotifOpen] = useState(false);
  const [annExpanded, setAnnExpanded] = useState(false);
  const annBodyRef = useRef(null);
  const [annClamped, setAnnClamped] = useState(false);
  useEffect(() => {
    const el = annBodyRef.current;
    if (el && !annExpanded) setAnnClamped(el.scrollHeight > el.clientHeight + 2);
  }, [announcements, annExpanded]);
  const loadNotif = () => api.get("/notifications").then((r) => setNotif(r.data)).catch(() => {});
  useEffect(() => { loadNotif(); const t = setInterval(loadNotif, 30000); return () => clearInterval(t); }, []);
  const openNotif = async () => { setNotifOpen(true); try { await api.post("/notifications/read"); } catch (e) {} loadNotif(); };

  const today = wibNow().toISOString().slice(0, 10);
  const [clockNow, setClockNow] = useState(wibNow());
  useEffect(() => { const t = setInterval(() => setClockNow(wibNow()), 1000); return () => clearInterval(t); }, []);
  const jam = `${String(clockNow.getHours()).padStart(2, "0")}:${String(clockNow.getMinutes()).padStart(2, "0")}:${String(clockNow.getSeconds()).padStart(2, "0")}`;
  const hariTanggal = clockNow.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const [selDate, setSelDate] = useState(today);

  useEffect(() => {
    api.get("/announcements").then((r) => setAnnouncements(r.data));
    api.get("/progress/catalog").then((r) => setSurahList(r.data.tahfidz || []));
    api.get("/gallery").then((r) => setGallery(r.data));
    api.get("/gallery/videos").then((r) => setVideos(r.data));
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/progress?student_id=${active.id}&category=tahfidz`).then((r) => setTahfidz(r.data));
    api.get(`/assessments?student_id=${active.id}`).then((r) => setAssessment(r.data[0] || null));
    api.get(`/attendance?student_id=${active.id}`).then((r) => setAttToday(r.data.find((a) => a.date === today) || null));
    api.get(`/reports/daily?student_id=${active.id}`).then((r) => setDailies(r.data));
    if (isSdqu) api.get(`/tahfidz/juz-progress?student_id=${active.id}`).then((r) => setJuzProgress(r.data)).catch(() => setJuzProgress([]));
    setSelDate(today);
  }, [active, today]);

  const lancarKeys = new Set(tahfidz.filter((p) => p.status === "lancar").map((p) => p.item_key));
  const hafalanDone = lancarKeys.size;
  const totalSurah = surahList.length || 37;
  const grp = groupOf(active?.kelas);

  // History: available dates from daily reports (newest first), always include today
  const dateSet = Array.from(new Set([today, ...dailies.map((d) => d.date)])).sort().reverse();
  const selDaily = dailies.find((d) => d.date === selDate) || null;
  const [evalThread, setEvalThread] = useState([]);
  const [kegThread, setKegThread] = useState([]);
  const loadDayThreads = () => {
    if (!active) return;
    api.get(`/progress?student_id=${active.id}&category=evaluasi_harian`).then((r) => setEvalThread((r.data.find((x) => x.item_key === selDate) || {}).thread || [])).catch(() => {});
    api.get(`/progress?student_id=${active.id}&category=kegiatan_harian`).then((r) => setKegThread((r.data.find((x) => x.item_key === selDate) || {}).thread || [])).catch(() => {});
  };
  useEffect(loadDayThreads, [active, selDate]); // eslint-disable-line
  const nextSurah = surahList.find((s) => !lancarKeys.has(s.key));
  const sortedD = [...dailies].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const lastHaf = sortedD.find((d) => d.hafalan_today);
  const lastKeg = sortedD.find((d) => d.note);
  const fmtFull = (d) => { try { return new Date(d).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); } catch { return d; } };
  const hafShow = selDaily?.hafalan_today
    ? { text: selDaily.hafalan_today, done: true }
    : (lastHaf ? { text: lastHaf.hafalan_today, done: true, date: lastHaf.date, last: true }
              : { text: (selDate === today ? (nextSurah?.label || "—") : "—"), done: false });
  const kegShow = selDaily?.note
    ? { text: selDaily.note }
    : (lastKeg ? { text: lastKeg.note, date: lastKeg.date, last: true } : null);

  const subKelas = active?.kelas || "";
  // Tiered fallback agar galeri depan tidak kosong: sub-kelas → kelas(grup) → Semua
  const pickTiered = (items) => {
    const grp = groupOf(subKelas);
    const t1 = items.filter((g) => g.kelas === subKelas);
    if (t1.length) return t1;
    const t2 = grp ? items.filter((g) => g.kelas === grp) : [];
    if (t2.length) return t2;
    return items.filter((g) => g.kelas === "Semua");
  };
  const galleryClass = pickTiered(gallery);
  const galleryVideos = pickTiered(videos);
  const [fullscreenIdx, setFullscreenIdx] = useState(null);
  const fsRef = useRef(null);
  const fotos = [...galleryClass].sort((a, b) => (b.created_at || b.date || "").localeCompare(a.created_at || a.date || "")).slice(0, 60);
  useEffect(() => {
    if (fullscreenIdx !== null && fsRef.current) {
      const el = fsRef.current;
      el.scrollTo({ left: fullscreenIdx * el.clientWidth, behavior: "instant" in Element.prototype ? "auto" : "auto" });
    }
  }, [fullscreenIdx]);
  const fmtChip = (d) => (d === today ? "Hari ini" : new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "short" }));

  return (
    <div className="pb-4" data-testid="parent-dashboard">
      {notifOpen && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setNotifOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 max-h-[80vh] overflow-y-auto" data-testid="notif-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Notifikasi</h3>
              <button onClick={() => setNotifOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            {notif.items.length === 0 ? (
              <p className="text-center text-stone-400 py-8 text-sm">Belum ada notifikasi.</p>
            ) : (
              <div className="space-y-2">
                {notif.items.map((n, i) => (
                  <button key={i} data-testid={`notif-item-${i}`} onClick={() => { setNotifOpen(false); navigate(`/siswa/${n.student_id}?tab=${n.category === "evaluasi_harian" ? "hafalan" : "laporan"}`); }}
                    className={`w-full text-left rounded-2xl p-3 border active:scale-[0.99] transition-transform ${n.read ? "bg-white border-stone-200/60" : "bg-primary/5 border-primary/30"}`}>
                    <p className="text-xs text-stone-400 mb-0.5">{n.category === "kegiatan_harian" ? "Catatan Guru" : n.category === "evaluasi_harian" ? "Hafalan" : n.item_label} · {n.student_name}</p>
                    <p className="text-sm text-stone-700"><b className="text-primary">{n.by_name || "Guru"}:</b> {n.reaction ? "👍 " : ""}{n.text || "(reaksi)"}</p>
                    <p className="text-[11px] text-stone-400 mt-1">{new Date(n.at).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · <span className="text-primary font-semibold">Lihat detail →</span></p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* 1. Header - green background (match teacher dashboard) */}
      <div className="bg-primary text-white px-5 pt-6 pb-8 rounded-b-[2rem] relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full" />
        <div className="flex items-center justify-between relative gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-white/95 flex items-center justify-center shrink-0 overflow-hidden p-0.5">
              <img src={isSdqu ? "/sdqu_logo.png" : "/tk_logo.png"} alt="Muslim Kids" className="w-full h-full object-contain" />
            </div>
            <h1 className="font-heading text-base font-bold leading-tight min-w-0">Assalamu'alaikum, Ayah/Bunda 👋</h1>
          </div>
          <div className="flex gap-2 shrink-0">
            <button data-testid="notif-btn" onClick={openNotif}
              className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center relative active:scale-90 transition-transform">
              <Bell size={20} weight="fill" />
              {notif.unread > 0 && <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-[10px] font-bold rounded-full flex items-center justify-center">{notif.unread}</span>}
            </button>
            <button data-testid="logout-icon-btn"
              onClick={async () => { await logout(); toast.success("Berhasil keluar"); navigate("/welcome"); }}
              className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center active:scale-90 transition-transform">
              <SignOut size={20} weight="bold" />
            </button>
          </div>
        </div>
        <div data-testid="parent-clock" className="flex items-center justify-between mt-4 relative">
          <span className="text-white/90 text-sm font-semibold">{hariTanggal}</span>
          <span className="flex items-center gap-1 bg-white/20 rounded-full px-3 py-1.5 text-sm font-bold shrink-0"><CalendarCheck size={15} weight="fill" /> {jam} WIB</span>
        </div>
      </div>

      {/* Welcome text - yellow box, 2 lines */}
      <div className="px-5 mt-4">
        <div data-testid="welcome-text" className="bg-[#FFF9C4] border border-yellow-200/70 rounded-2xl px-4 py-3">
          <p className="font-bold text-sm text-stone-800 leading-snug">Selamat datang di {isSdqu ? "SDQu" : "TK"} Muslim Kids App</p>
          <p className="font-semibold text-xs text-stone-600 leading-snug mt-0.5">Pantau tumbuh kembang Ananda dengan mudah</p>
        </div>
      </div>

      {/* 2. Child profile card */}
      <div className="px-5 mt-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-5 shadow-sm border border-stone-200/60">
          <div className="flex items-center gap-4">
            <KidAvatar gender={active?.gender} size={104} src={active?.avatar} className="ring-2 ring-primary/10 shrink-0" />
            <div className="flex-1">
              <p className="text-stone-400 text-xs">Ananda</p>
              <h2 className="font-heading text-base font-bold text-stone-800 leading-tight">{active?.name || "—"}</h2>
              <p className="text-stone-500 text-sm">Kelas {active?.kelas || "—"}{active?.nis ? ` • ${active.nis}` : ""}</p>
              {active?.teacher_name && <p className="text-primary text-sm font-medium mt-0.5" data-testid="umi-guru-name">Umi Guru {active.teacher_name}</p>}
            </div>
          </div>
          {students.length > 1 && (
            <div className="flex gap-2 mt-3">
              {students.map((s) => (
                <button key={s.id} onClick={() => selectStudent(s.id)}
                  className={`text-xs px-3 py-1 rounded-full ${s.id === active?.id ? "bg-primary text-white" : "bg-stone-100 text-stone-500"}`}>
                  {s.name.split(" ")[0]}
                </button>
              ))}
            </div>
          )}
          <button data-testid="lihat-profil-anak" onClick={() => active && navigate(`/siswa/${active.id}`)} disabled={!active}
            className="mt-4 w-full border-2 border-primary text-primary text-sm font-semibold px-4 py-2.5 rounded-full inline-flex items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-60">
            {active ? "Absensi & Evaluasi Anak" : "Belum ada data ananda"} <CaretRight size={14} weight="bold" />
          </button>
        </motion.div>
      </div>

      {/* 3. Stats */}
      <div className="px-5 grid grid-cols-4 gap-2 mt-4">
        <Stat icon={BookOpen} label={`${hafalanDone}/${totalSurah}`} value="Materi" tone="text-purple-600" bg="bg-purple-100" onClick={() => navigate("/hafalan")} testid="stat-hafalan" />
        <Stat icon={CalendarCheck} label="Hari Ini" value="Jadwal" tone="text-rose-500" bg="bg-rose-100" onClick={() => navigate("/jadwal")} testid="stat-jadwal" />
        <Stat icon={Wallet} label="Ananda" value="SPP" tone="text-orange-500" bg="bg-orange-100" onClick={() => navigate("/spp")} testid="stat-spp" />
        <Stat icon={Star} label="Predikat" value={assessment?.predikat || "—"} tone="text-yellow-500" bg="bg-yellow-100" onClick={() => navigate("/perkembangan")} testid="stat-predikat" />
      </div>

      {/* 4. Announcement */}
      {announcements[0] && (
        <Section title="Pengumuman Terbaru" icon={Megaphone} iconTone="text-secondary" action={() => navigate("/info")} actionLabel="Lihat Semua" testid="lihat-semua-info">
          <div className="bg-secondary/15 border border-secondary/30 rounded-3xl p-5" data-testid="pengumuman-terbaru">
            <p className="font-heading font-bold text-stone-800 text-lg leading-snug">{announcements[0].title}</p>
            <p ref={annBodyRef} className={`text-stone-600 text-sm mt-2 leading-relaxed whitespace-pre-line ${annExpanded ? "" : "line-clamp-4"}`}>{announcements[0].body}</p>
            {(annClamped || annExpanded) && (
              <button data-testid="pengumuman-selengkapnya" onClick={() => setAnnExpanded((v) => !v)}
                className="mt-2 text-primary text-sm font-semibold active:scale-95 transition-transform">
                {annExpanded ? "Tutup" : "Selengkapnya"}
              </button>
            )}
          </div>
        </Section>
      )}

      {/* 4b. SPP moved to dedicated page (/spp), accessed via stat shortcut */}

      {/* 5. Riwayat (date selector) */}
      <div className="px-5 mt-6">
        <h3 className="font-heading font-semibold text-stone-800 mb-2.5">Riwayat Harian</h3>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="parent-date-chips">
          {dateSet.slice(0, 14).map((d) => (
            <button key={d} data-testid={`date-chip-${d}`} onClick={() => setSelDate(d)}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${selDate === d ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-500"}`}>
              {fmtChip(d)}
            </button>
          ))}
        </div>
      </div>

      {/* 6. Hafalan (selected date) */}
      <Section title="Evaluasi Belajar" icon={BookOpen} iconTone="text-primary" action={() => navigate("/hafalan")} actionLabel="Materi Evaluasi" testid="materi-evaluasi-btn">
        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Hafalan Hari Ini</p>
        <div data-testid="hafalan-today" className="bg-white rounded-2xl border border-stone-200/60 p-4 shadow-sm">
          <span className="font-semibold text-stone-800 text-sm whitespace-pre-line">{hafShow.text}</span>
          {hafShow.date && <p className="text-[11px] text-stone-400 mt-0.5">Terakhir: {fmtFull(hafShow.date)}</p>}
        </div>
        {active && <div className="bg-white rounded-2xl border border-stone-200/60 px-4 pb-2 shadow-sm mt-2"><ReactionThread studentId={active.id} category="evaluasi_harian" itemKey={selDate} itemLabel={`Evaluasi ${selDate}`} thread={evalThread} onPosted={loadDayThreads} /></div>}
      </Section>

      {/* 7. Kegiatan (selected date) */}
      <Section title={`Kegiatan ${selDate === today ? "Hari Ini" : fmtChip(selDate)}`} icon={Sparkle} iconTone="text-amber-500">
        <div data-testid="kegiatan-today" className="bg-white rounded-2xl border border-stone-200/60 p-4 shadow-sm">
          {kegShow ? (
            <>
              <p className="text-stone-700 text-sm leading-relaxed whitespace-pre-line">{kegShow.text}</p>
              {kegShow.date && <p className="text-[11px] text-stone-400 mt-1">Terakhir dinilai guru: {fmtFull(kegShow.date)}</p>}
            </>
          ) : (
            <p className="text-stone-400 text-sm">Belum ada kegiatan.</p>
          )}
          {active && <ReactionThread studentId={active.id} category="kegiatan_harian" itemKey={selDate} itemLabel={`Kegiatan ${selDate}`} thread={kegThread} onPosted={loadDayThreads} />}
        </div>
      </Section>

      {/* 7b. Capaian Tahfidz per Juz (SD) */}
      {isSdqu && juzProgress.length > 0 && (
        <Section title="Capaian Tahfidz per Juz" icon={BookOpen} iconTone="text-primary">
          <div className="space-y-2" data-testid="juz-progress-list">
            {juzProgress.map((j) => {
              const pct = j.total_surah ? Math.round((j.lancar_surah / j.total_surah) * 100) : 0;
              return (
                <div key={j.juz} data-testid={`juz-progress-${j.juz}`} className="bg-white rounded-2xl border border-stone-200/60 p-3.5 shadow-sm">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-heading font-bold text-stone-800 text-sm">Juz {j.juz}</span>
                    <span className="text-xs font-bold text-primary">{j.lancar_surah}/{j.total_surah} surah Lancar</span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Video Pembelajaran (di atas Galeri) */}
      <LearningMediaCarousel />

      {/* 8. Galeri kelas */}
      <Section title={`Galeri ${subKelas || "Kelas"}`} icon={Images} iconTone="text-purple-500" action={() => navigate("/galeri")} actionLabel="Lihat Semua Galeri" testid="lihat-semua-galeri">
        <GalleryCarousel videos={galleryVideos} photos={fotos} onFullscreen={setFullscreenIdx} />
      </Section>

      {fullscreenIdx !== null && (
        <div data-testid="gallery-fullscreen" className="fixed inset-0 z-[80] bg-black/90 animate-in fade-in">
          <button data-testid="gallery-fullscreen-close" onClick={() => setFullscreenIdx(null)}
            className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center active:scale-90 transition-transform">
            <X size={24} weight="bold" />
          </button>
          <div ref={fsRef} data-testid="gallery-fullscreen-scroll"
            className="w-full h-full flex overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
            {fotos.map((f, i) => (
              <div key={i} className="w-full h-full shrink-0 snap-center flex items-center justify-center p-4">
                <img src={f.url} alt="galeri" loading="lazy" className="max-w-full max-h-full object-contain rounded-2xl" />
              </div>
            ))}
          </div>
          <div className="absolute bottom-6 left-0 right-0 text-center text-white/70 text-xs">Geser ke samping untuk foto lainnya</div>
        </div>
      )}

      <div className="h-4" />
    </div>
  );
}

function Section({ title, icon: Icon, iconTone, action, actionLabel, testid, children }) {
  return (
    <div className="px-5 mt-6">
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="font-heading font-semibold text-stone-800 flex items-center gap-2">
          <Icon size={24} weight="fill" className={iconTone} /> {title}
        </h3>
        {action && (
          <button onClick={action} data-testid={testid} className="bg-primary text-white text-xs font-semibold px-3 py-1.5 rounded-full active:scale-95 transition-transform">{actionLabel}</button>
        )}
      </div>
      {children}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, bg, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="bg-white rounded-2xl border border-stone-200/60 p-2 flex flex-col items-center gap-1 shadow-sm active:scale-95 transition-transform">
      <div className={`rounded-xl ${bg} flex items-center justify-center`} style={{ width: 38, height: 38 }}>
        <Icon size={20} weight="fill" className={tone} />
      </div>
      <span className="font-heading font-bold text-stone-800 text-xs leading-tight text-center line-clamp-2 max-w-full">{value}</span>
      <span className="text-[9px] text-stone-400 text-center leading-tight line-clamp-1">{label}</span>
    </button>
  );
}
